import React, { useState, useEffect } from "react";
import { Upload, Trash2, Star, X } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";
import { useInteriorDesignCategory } from "../../../Containers/interior-designs/useHook";

const InteriorDesignModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditing = false,
  editData = null,
  isSubmitting = false
}) => {
  const { getAllInteriorDesignCategories } = useInteriorDesignCategory();
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    customUrl: "",
    plot_dimension: "",
    covered_area: "",
    floors: 0,
    bed_rooms: 0,
    bath_rooms: 0,
    category_id: "",
    room_size: "",
    design_type: "",
    images: [],
    imagePreviews: [],
    primaryImageIndex: 0,
    features: {
      carParking: false,
      terrace: false,
      servantQuarter: false,
    }
  });

  const [imageError, setImageError] = useState("");
  const [existingImages, setExistingImages] = useState([]);

  useEffect(() => {
    if (editData && isEditing) {
      setFormData({
        title: editData.title || "",
        description: editData.description || "",
        customUrl: editData.custom_url || editData.customUrl || "",
        plot_dimension: editData.plot_dimension || "",
        covered_area: editData.covered_area || "",
        floors: editData.floors || 0,
        bed_rooms: editData.bed_rooms || 0,
        bath_rooms: editData.bath_rooms || 0,
        category_id: editData.category_id || "",
        room_size: editData.room_size || "",
        design_type: editData.design_type || "",
        images: [],
        imagePreviews: [],
        primaryImageIndex: 0,
        features: {
          carParking: editData.car_parking || editData.features?.carParking || false,
          terrace: editData.terrace || editData.features?.terrace || false,
          servantQuarter: editData.servant_quarters || editData.features?.servantQuarter || false,
        }
      });

      setExistingImages(editData.existingImages || editData.images || []);
    } else {
      resetForm();
    }
  }, [editData, isEditing, isOpen]);

  const resetForm = () => {
    if (formData.imagePreviews) {
      formData.imagePreviews.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    }

    setFormData({
      title: "",
      description: "",
      customUrl: "",
      plot_dimension: "",
      covered_area: "",
      floors: 0,
      bed_rooms: 0,
      bath_rooms: 0,
      category_id: "",
      room_size: "",
      design_type: "",
      images: [],
      imagePreviews: [],
      primaryImageIndex: 0,
      features: {
        carParking: false,
        terrace: false,
        servantQuarter: false,
      }
    });
    setExistingImages([]);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      const response = await getAllInteriorDesignCategories();
      setCategories(response);
    };
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : type === "number" ? parseInt(value) || 0 : value,
    });
  };

  const handleQuillChange = (value) => {
    setFormData({
      ...formData,
      description: value,
    });
  };

  const handleFeatureChange = (featureName) => {
    setFormData({
      ...formData,
      features: {
        ...formData.features,
        [featureName]: !formData.features[featureName],
      },
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newValidImages = [];
    const newImagePreviews = [];

    const checkImageResolution = (file) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width === 1200 && img.height === 900) {
            resolve(true);
          } else {
            resolve(false);
          }
        };
        img.src = URL.createObjectURL(file);
      });
    };

    Promise.all(files.map((file) => checkImageResolution(file))).then(
      (results) => {
        results.forEach((isValid, index) => {
          if (isValid) {
            newValidImages.push(files[index]);
            newImagePreviews.push(URL.createObjectURL(files[index]));
          } else {
            alert(
              `Image "${files[index].name}" does not have the required resolution of 1200*900. Please upload the correct resolution.`
            );
          }
        });
        setFormData((prev) => {
          const currentImageCount = prev.images ? prev.images.length : 0;
          const newPrimaryIndex = currentImageCount === 0 ? 0 : prev.primaryImageIndex;

          return {
            ...prev,
            images: [...(prev.images || []), ...newValidImages],
            imagePreviews: [...(prev.imagePreviews || []), ...newImagePreviews],
            primaryImageIndex: newPrimaryIndex,
          };
        });
      }
    );
  };

  const removeImage = (index, isExisting = false) => {
    if (isExisting) {
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      const updatedImages = [...formData.images];
      const updatedPreviews = [...(formData.imagePreviews || [])];

      if (updatedPreviews[index]) {
        URL.revokeObjectURL(updatedPreviews[index]);
      }

      updatedImages.splice(index, 1);
      updatedPreviews.splice(index, 1);

      let newPrimaryIndex = formData.primaryImageIndex;
      if (index <= formData.primaryImageIndex) {
        newPrimaryIndex = Math.max(0, formData.primaryImageIndex - 1);
      }

      setFormData({
        ...formData,
        images: updatedImages,
        imagePreviews: updatedPreviews,
        primaryImageIndex: newPrimaryIndex,
      });

      if (updatedImages.length === 0 && existingImages.length === 0) {
        setImageError("At least one image is required");
      }
    }
  };

  const setPrimaryImage = (index) => {
    setFormData({
      ...formData,
      primaryImageIndex: index,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.customUrl || formData.customUrl.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL is required. Please enter a custom URL.",
      });
      return;
    }

    if (formData.customUrl.trim().startsWith('/')) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL cannot start with a forward slash (/). Please enter a valid URL.",
      });
      return;
    }

    if ((!formData.images || formData.images.length === 0) && (!existingImages || existingImages.length === 0)) {
      setImageError("At least one image is required");
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please upload at least one image before submitting.",
      });
      return;
    }

    const formDataWithExistingImages = {
      ...formData,
      existingImages: existingImages
    };

    onSubmit(formDataWithExistingImages);

    // Reset form after successful submission (only for new additions, not for editing)
    if (!isEditing) {
      resetForm();
    }
  };

  const handleClose = () => {
    if (formData.imagePreviews) {
      formData.imagePreviews.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    }
    resetForm();
    onClose();
  };

  useEffect(() => {
    return () => {
      if (formData.imagePreviews) {
        formData.imagePreviews.forEach((url) => {
          URL.revokeObjectURL(url);
        });
      }
    };
  }, [formData.imagePreviews]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-3/4 max-w-6xl relative max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Edit Interior Design" : "Add Interior Design"}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          {/* Basic Information */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="customUrl"
                  value={formData.customUrl}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  placeholder="Select Category"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Interior Design Information */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plot Dimension *
                </label>
                <input
                  type="text"
                  name="plot_dimension"
                  value={formData.plot_dimension}
                  onChange={handleChange}
                  placeholder="e.g., 30x50 ft"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div> */}

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Covered Area *
                </label>
                <input
                  type="text"
                  name="covered_area"
                  value={formData.covered_area}
                  onChange={handleChange}
                  placeholder="e.g., 1500 sq ft"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div> */}

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Floors *
                </label>
                <input
                  type="number"
                  name="floors"
                  value={formData.floors}
                  onChange={handleChange}
                  min="0"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bedrooms *
                </label>
                <input
                  type="number"
                  name="bed_rooms"
                  value={formData.bed_rooms}
                  onChange={handleChange}
                  min="0"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bathrooms *
                </label>
                <input
                  type="number"
                  name="bath_rooms"
                  value={formData.bath_rooms}
                  onChange={handleChange}
                  min="0"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div> */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Room Size *
                </label>
                <input
                  type="text"
                  name="room_size"
                  value={formData.room_size}
                  onChange={handleChange}
                  placeholder="e.g., 12x15 ft"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Design Type *
                </label>
                <input
                  type="text"
                  name="design_type"
                  value={formData.design_type}
                  onChange={handleChange}
                  placeholder="e.g., Modern, Traditional, Contemporary"
                  className="w-full border border-gray-300 rounded-md p-2"
                  maxLength={15}
                  required
                />
              </div>

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Associate to Category *
                </label>
                <select
                  name="associated_house"
                  value={formData.associated_house}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                >
                  <option value="">Select category</option>
                  <option value="3 Marla">3 Marla</option>
                  <option value="5 Marla">5 Marla</option>
                  <option value="8 Marla">8 Marla</option>
                  <option value="10 Marla">10 Marla</option>
                  <option value="1 kanal">1 Kanal</option>
                  <option value="2 kanal">2 Kanal</option>
                  <option value="others">Others</option>
                </select>
              </div> */}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <div className="rounded-md">
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={handleQuillChange}
                className="h-40"
              />
            </div>
          </div>

          {/* Features Checkboxes */}
          {/* <div className="mb-6 mt-14">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Features
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.keys(formData.features).map((feature) => (
                <div
                  key={feature}
                  className="flex items-center space-x-3 bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors duration-200"
                >
                  <input
                    type="checkbox"
                    id={feature}
                    name={feature}
                    checked={formData.features[feature]}
                    onChange={() => handleFeatureChange(feature)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor={feature}
                    className="text-sm font-medium text-gray-700 cursor-pointer select-none capitalize flex-1"
                  >
                    {feature === 'carParking'
                      ? 'Car Parking'
                      : feature === 'servantQuarter'
                        ? 'Servant Quarter'
                        : feature === 'terrace'
                          ? 'Terrace'
                          : feature.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                </div>
              ))}
            </div>
          </div> */}

          {/* Existing Images Display */}
          {isEditing && existingImages.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Images
              </label>
              <div className="grid grid-cols-4 gap-2">
                {existingImages.map((image, index) => (
                  <div key={image.id || index} className="relative group">
                    <div className="h-24 border rounded-md flex items-center justify-center bg-gray-100 overflow-hidden">
                      <img
                        src={image.image_url}
                        alt={`Existing image ${index + 1}`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    {image.is_primary && (
                      <div className="absolute top-1 left-1 bg-blue-500 text-white rounded-full p-1">
                        <Star size={12} fill="white" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeImage(index, true)}
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-4 pt-8">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload Images <span className="text-lg text-red-500">*</span>
            </label>
            <div className="border border-dashed border-gray-300 p-4 rounded-md relative group">
              <input
                type="file"
                multiple
                onChange={handleImageChange}
                className="hidden"
                id="images"
                accept="image/*"
              />
              <label
                htmlFor="images"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <Upload size={24} className="text-gray-500 mb-2" />
                <span className="text-sm text-gray-500">
                  Click to upload multiple images
                </span>
              </label>
              {/* Tooltip */}
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
                <div className="bg-gray-800 text-white text-xs rounded-md py-2 px-3 whitespace-nowrap shadow-lg">
                  Only the image with 1200*900 resolution can be added
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Display selected images */}
          {formData.images.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Selected Images ({formData.images.length})
              </h4>
              <div className="grid grid-cols-4 gap-2">
                {formData.images.map((file, index) => (
                  <div key={index} className="relative">
                    <div className={`h-24 border rounded-md flex items-center justify-center bg-gray-100 overflow-hidden ${index === formData.primaryImageIndex ? 'ring-2 ring-blue-500' : ''
                      }`}>
                      {formData.imagePreviews &&
                        formData.imagePreviews[index] ? (
                        <img
                          src={formData.imagePreviews[index]}
                          alt={file.name || `Image ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <p className="text-xs text-gray-500 p-2 truncate">
                          {file.name || "Image " + (index + 1)}
                        </p>
                      )}
                    </div>

                    {/* Primary Image Star */}
                    {index === formData.primaryImageIndex && (
                      <div className="absolute top-1 left-1 bg-blue-500 text-white rounded-full p-1">
                        <Star size={12} fill="white" />
                      </div>
                    )}

                    {/* Remove Button */}
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                    >
                      <Trash2 size={12} />
                    </button>

                    {/* Set Primary Button */}
                    {index !== formData.primaryImageIndex && (
                      <button
                        type="button"
                        onClick={() => setPrimaryImage(index)}
                        className="absolute bottom-1 left-1 bg-green-500 text-white rounded-full p-1 hover:bg-green-600 transition-colors"
                        title="Set as Primary Image"
                      >
                        <Star size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Primary Image Info */}
              {formData.images.length > 1 && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-700">
                    <Star size={12} className="inline mr-1" />
                    Primary image selected: {formData.images[formData.primaryImageIndex]?.name || `Image ${formData.primaryImageIndex + 1}`}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-2 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-gray-200 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 bg-blue-500 text-white rounded-md flex items-center ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : isEditing ? (
                "Update"
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InteriorDesignModal;
